import { z } from 'zod';
import { dateSchema, objectIdSchema, pricePositiveSchema } from '../validation';
import { currencySchema } from './currency.schema';

export type HyperPayCheckoutResponse = {
  result: {
    /** the code of the result such as `000.200.100` */
    code: string;
    /** the description of the result such as `successfully created checkout` */
    description: string;
  };
  /** the build number of hyperpay */
  buildNumber: string;
  /** the timestamp of  hyperpay */
  timestamp: string;
  /** the ndc of hyperpay*/
  ndc: string;
  /** the checkout id of hyperpay */
  id: string;
};

export const paymentMethodsHyperPaySchema = z.enum(['APPLEPAY', 'MADA', 'VISA', 'MASTER']);
export type PaymentMethodsHyperPay = z.infer<typeof paymentMethodsHyperPaySchema>;

export const hyperPayWebhookCustomPayloadSchema = z.object({
  /** payment id from hyper pay */
  id: z.coerce.string(),
  /** the checkout id from hyperpay.
   *
   * It will come from the `ndc` field in the response.
   */
  checkoutId: z.coerce.string(),
  /** the amount of the payment */
  amount: pricePositiveSchema,
  /** the currency of the payment */
  currency: currencySchema,
  /** whether the payment was made with apple pay. */
  isApplePay: z.boolean(),

  /** the order id that payment was paid for.
   *
   * It's optional because sometimes the user pays for a invoice not a single order or sometimes pay all due invoices/orders at once.
   */
  orderId: objectIdSchema.optional(),
  /** the invoice id that payment was paid for.
   *
   * It's optional because sometimes the user pays for a specific order not a hole invoice or sometimes pay all due invoices at once.
   */
  invoiceId: objectIdSchema.optional(),

  /** the user id that payment was paid for coming from `User` Collection */
  userId: objectIdSchema,

  /**
   * the organization id that payment was paid for coming from `Business` or `Carrier` Collection.
   *
   * It's optional because some users don't belong to any organization such as individual customers.
   */
  orgId: objectIdSchema.optional(),

  /** the payment brand such as `VISA` */
  paymentBrand: paymentMethodsHyperPaySchema,
  /** the result code of the payment */
  result: z.object({
    code: z.coerce.string(),
    description: z.coerce.string(),
  }),

  /** the timestamp of the payment */
  timestamp: dateSchema,
});

export type HyperPayWebhookCustomPayload = z.infer<typeof hyperPayWebhookCustomPayloadSchema>;

export interface HyperPayPaymentData {
  /** if empty, then, is not yet created ... or pending */
  id?: string;
  paymentType?: string;
  paymentBrand?: string;
  amount?: string;
  currency?: string;
  presentationAmount?: string;
  presentationCurrency?: string;
  descriptor?: string;
  result?: {
    code?: string;
    description?: string;
  };
  authentication?: {
    entityId?: string;
  };
  card?: {
    bin?: string;
    last4Digits?: string;
    holder?: string;
    expiryMonth?: string;
    expiryYear?: string;
  };
  customer?: {
    givenName?: string;
    surname?: string;
    merchantCustomerId?: string;
    sex?: string;
    email?: string;
  };
  /** this where we can have more custom parameters that we want to send to the hyperpay.
   *
   * Any field that starts with `SHOPPER_` it means it's a custom parameter that we want to send to hyper pay and saved under `customParameters` object in the response.
   */
  customParameters: {
    /** the source of apple pay such as `In-App`.
     *
     * It's empty because some users pay without apple pay. So it this exists, it means the user paid with apple pay.
     */
    APPLEPAY_Source?: 'In-App';
    /** the token version of apple pay such as `1`.
     *
     * It's empty because some users pay without apple pay. So it this exists, it means the user paid with apple pay.
     */
    APPLEPAY_TokenVersion?: string;

    /** the user id coming from `User` Collection */
    SHOPPER_userId: string;

    /** the org id coming from `Business` or `Carrier` Collection.
     *
     * It's optional because some users don't belong to any organization such as individual customers.
     */
    SHOPPER_orgId?: string;

    /** the invoice id coming from `Invoice` Collection.
     *
     * It's optional because sometimes the user pays for all due invoices/orders at once not a single invoice/order.
     */
    SHOPPER_invoiceId?: string;

    /** the order id coming from `Order` Collection.
     *
     * It's optional because sometimes the user pays for all due invoices/orders at once not a single order.
     */
    SHOPPER_orderId?: string;
  };
  risk?: {
    score?: string;
  };
  buildNumber?: string;
  timestamp?: string;
  ndc?: string;
  channelName?: string;
  source?: string;
  paymentMethod?: string;
  shortId?: string;
  [key: string]: any;
}

export type HyperPayWebhook = {
  /** this is where the actual payment info is stored such as the amount */
  payload: HyperPayPaymentData;

  // @TBC -- this might change in production and added more types
  type: 'PAYMENT';
};
